import { useRef, useState, useCallback } from 'react'
import { TextInput, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import Animated, {
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated'
import Input from '@ui/forms/Input'
import Button from '@ui/buttons/Button'
import SafePressable from '@ui/buttons/SafePressable'
import { Collapsible } from '@ui/animated/Collapsible'
import { ToggleIcon } from '@ui/forms/Select/ToggleIcon'
import Line from '@ui/elements/Line/Line'
import useAnimatedToggle from '@ui/hooks/useAnimatedToggle'
import { router, useLocalSearchParams } from 'expo-router'
import { useDebouncedCallback } from 'use-debounce'
import { colors } from '@ui/colors'

const SEARCH_DEBOUNCE_MS = 1500

interface Props {
  label: string
  placeholder: string
  isFetching?: boolean
  children?: React.ReactNode
}

export default function SearchBar({
  label,
  placeholder,
  isFetching = false,
  children,
}: Props) {
  const { t } = useTranslation(['ui'])

  const { q } = useLocalSearchParams<{ q: string }>()
  const [localQuery, setLocalQuery] = useState(q || '')
  const [isExpanded, setIsExpanded] = useState(false)
  const inputRef = useRef<TextInput>(null)

  const expandAnimation = useAnimatedToggle(isExpanded)
  const hasChildren = !!children

  const triggerSearch = useCallback((value: string) => {
    router.setParams({ q: value })
  }, [])

  const debouncedSearch = useDebouncedCallback((value: string) => {
    triggerSearch(value)
  }, SEARCH_DEBOUNCE_MS)

  const handleChangeText = useCallback(
    (text: string) => {
      setLocalQuery(text)
      debouncedSearch(text)
    },
    [debouncedSearch]
  )

  const handleSearchPress = useCallback(() => {
    debouncedSearch.cancel()
    triggerSearch(localQuery)
    inputRef.current?.blur()
  }, [debouncedSearch, localQuery, triggerSearch])

  const handleToggleExpand = useCallback(() => {
    setIsExpanded((v) => !v)
  }, [])

  const separatorStyle = useAnimatedStyle(() => ({
    opacity: interpolate(expandAnimation.value, [0, 0.5], [0, 1]),
  }))

  return (
    <View className="overflow-hidden rounded-xl border border-black/10 bg-white p-8">
      <View className="flex-row items-center gap-8">
        <View className="flex-1">
          <Input
            ref={inputRef}
            label={label}
            placeholder={placeholder}
            value={localQuery}
            autoCorrect={false}
            onChangeText={handleChangeText}
            onSubmitEditing={handleSearchPress}
            returnKeyType="search"
          />
        </View>
        <Button
          label={t('ui:search')}
          variant="outlined"
          iconEnd="search"
          loading={isFetching && q === localQuery}
          onPress={handleSearchPress}
        />
        {hasChildren && (
          <SafePressable onPress={handleToggleExpand}>
            <Animated.View className="h-12 w-12 items-center justify-center rounded-full border border-black/10">
              <ToggleIcon
                animation={expandAnimation}
                size={20}
                color={colors.text}
              />
            </Animated.View>
          </SafePressable>
        )}
      </View>

      {hasChildren && (
        <>
          <Animated.View style={separatorStyle} className="px-3">
            <Line />
          </Animated.View>
          <Collapsible open={isExpanded} fullWidth>
            <View className="p-3 pt-0">{children}</View>
          </Collapsible>
        </>
      )}
    </View>
  )
}
